import DataGrid, { DataGridConfig } from 'components/DataGrid';
import JsonToTable from 'components/JsonToTable';
import PageLayout from 'components/PageLayout';
import RepoTypeIcon from 'components/RepoTypeIcon';
import { useAtom, useAtomValue } from 'jotai';
import { isAdminUserLoggedIn } from 'lib/helper';
import React, { Suspense, useMemo, useState } from 'react';
import { Row, Col, CardTitle } from 'reactstrap';
import { getDrillDownUrl } from 'views/Admin/Dashboard/helper';
import { appliedRowIdsAtom } from 'views/Admin/Dashboard/SASTDashboard2/state';

import Actions from './Actions';
import Filters from './Filters';
import RepositoryBranchColumnContents from './RepositoryBranchColumnContents';
import RepositoryBranchTooltip from './RepositoryBranchTooltip';
import {
  enabledColumnKeysAtom,
  forceRerenderUIAtom,
  sortOrderAtom,
  orderbyAtom,
  paginationConfigAtom,
  repositoriesDataAtom,
  selectedRowIdsAtom,
  DEFAULT_REPOSITORY_LISTING_COLUMNS,
} from './state';
import StatusUpdater from './StatusUpdater';
import history from '../../../history.js';
import AccountDetailModal from '../Accounts/AccountDetailModal';
import useActivateDeactivateAccount from '../Accounts/hooks';
import AccountInfoSummary from '../components/AccountInfoSummary';
import LanguageBadges from '../components/RepositoryLanguages';
import RepoStatusBar from '../components/RepoStatusBar';
import UserInfoSummary from '../components/UserInfoSummary';
import { getHostLabel } from '../helper';

const RepositoryList = () => {
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const [orderby, setOrderBy] = useAtom(orderbyAtom);
  const [sortOrder, setSortOrder] = useAtom(sortOrderAtom); // Sort order

  const appliedSASTIds = useAtomValue(appliedRowIdsAtom);

  const { loading: accountActionLoading, handleActivateDeactivateAccount } =
    useActivateDeactivateAccount();
  const [accountDetailsModal, setAccountDetailsModal] = useState({
    open: false,
    accountId: 0,
    isActive: false,
    accountRowData: null,
    toggle: () =>
      setAccountDetailsModal({ ...accountDetailsModal, open: false }),
  });

  const listingData = useAtomValue(repositoriesDataAtom);
  const [enabledColumnKeys, updateEnabledColumnKeys] = useAtom(
    enabledColumnKeysAtom,
  );
  const [selectedRowIds, setSelectedRowIds] = useAtom(selectedRowIdsAtom);
  const forceRerenderUI = useAtomValue(forceRerenderUIAtom);
  const [loading, setLoading] = useState(false);

  const enableSelectionAcrossPages = true;

  const isWithinLastMinute = (datetime: string): boolean =>
    Date.now() - new Date(datetime).getTime() <= 60000;

  const getReposGridConfig = (): DataGridConfig => ({
    rowIdKey: 'id',
    columns: [
      {
        key: 'id',
        label: 'Id',
        sortable: true,
        headerTooltip: 'Row Id in database.',
      },
      {
        key: 'type',
        label: 'Type',
        sortable: true,
        orderBy: 'account__host_type',
        tooltip: (rowData) =>
          `${getHostLabel(rowData['account']['host_type'])} Repository`,
        overrideContents: (rowData) => (
          <RepoTypeIcon repoType={rowData['account']['host_type']} size={24} />
        ),
        headerTooltip: 'Host type.',
      },
      {
        key: 'name',
        label: 'Repository Name',
        sortable: true,
        // NOTE: auto width for one column to expand automatically
        headerCellStyle: { width: 'auto' },
        rowCellStyle: { width: 'auto' },
        headerTooltip: 'Repository name.',
      },
      {
        key: 'group_name',
        label: 'Group',
        sortable: false,
        headerTooltip: 'Gitlab Repository Group',
      },
      {
        key: 'branch',
        label: 'Branch',
        sortable: true,
        headerTooltip: 'Repository branch name.',
      },
      {
        key: 'organization',
        label: 'Organization',
        sortable: false,
        headerTooltip: 'Github/Azure DevOps Repository Organization',
      },
      {
        key: 'project',
        label: 'Project',
        sortable: false,
        headerTooltip: 'Azure DevOps Repository Project',
      },
      {
        key: 'workspace',
        label: 'Workspace',
        sortable: false,
        headerTooltip: 'Bitbucket Repository Workspace',
      },
      // eslint-disable-next-line no-warning-comments
      // TODO: other repository specific fields for other repo types
      {
        key: 'repo_branch',
        label: 'Repository/Branch',
        sortable: true,
        orderBy: 'name,branch',
        overrideContents: (rowData) => (
          <RepositoryBranchColumnContents rowData={rowData} />
        ),
        headerCellStyle: { width: 'auto' },
        rowCellStyle: {
          width: 'auto',
          textDecoration: 'none',
          whiteSpace: 'nowrap',
        },
        headerTooltip: <RepositoryBranchTooltip />,
      },
      {
        key: 'account',
        label: 'Account',
        sortable: true,
        orderBy: 'account__title',
        headerTooltip: 'Account information.',
        tooltip: (rowData) => (
          <AccountInfoSummary
            props={rowData['account']}
            description="Account for this repository."
            mode="tooltip"
          />
        ),
        overrideContents: (rowData) => rowData['account']['title'],
        onClick: (rowData) => {
          setAccountDetailsModal((prev) => ({
            ...prev,
            accountId: rowData['account']['id'],
            isActive: rowData['account']['is_active'],
            open: true,
          }));
        },
      },
      {
        key: 'added_by',
        label: 'Added By',
        sortable: true,
        orderBy: 'added_by__first_name,added_by__last_name',
        overrideContents: (rowData) =>
          `${rowData['added_by']['first_name']} ${rowData['added_by']['last_name']}`,
        tooltip: (rowData) => (
          <UserInfoSummary
            props={rowData['added_by']}
            description="Security Program 360 user who connected the repository."
          />
        ),
      },
      { key: 'is_active', label: 'Active', sortable: true },
      { key: 'created_at', label: 'Created At', sortable: true },
      {
        key: 'updated_at',
        label: 'Updated At',
        sortable: true,
        headerTooltip: 'Last Updated in SP360.',
      },
      {
        key: 'status',
        label: 'Repository Status',
        sortable: true,
        overrideContents: (rowData) => (
          <RepoStatusBar status={rowData['status']} />
        ),
        tooltip: (rowData) =>
          isAdminUserLoggedIn() ? (
            <JsonToTable
              data={rowData['status_history']}
              cssStyles={{ maxWidth: '600px' }}
            ></JsonToTable>
          ) : rowData?.status_history?.length > 0 ? (
            rowData?.status_history[0].description
          ) : undefined,
      },
      { key: 'last_commit_date', label: 'Code Updated At', sortable: true },
      {
        key: 'scan__last_sync_completed_at',
        label: 'Last Scanned At',
        sortable: true,
        orderBy: 'scan__last_sync_completed_at',
        headerTooltip: 'Last scanned on datetime',
        overrideContents: (rowData) => rowData?.scan?.last_sync_completed_at,
        rowCellStyle: { textAlign: 'center' },
      },
      {
        key: 'uuid',
        label: 'UUID',
        sortable: true,
        headerTooltip: 'Repository unique Id internally used by SP360.',
      },
      {
        key: 'archived',
        label: 'Archived',
        headerTooltip: 'Repository archive status.',
      },
      {
        key: 'new_violations',
        label: 'New Violations',
        sortable: true,
        orderBy: 'scan__new_violations',
        headerTooltip: 'New Violations.',
        overrideContents: (rowData) => rowData?.scan?.new_violations,
        rowCellStyle: { textAlign: 'center' },
      },
      {
        key: 'blocker_violations',
        label: 'Blocker Violations',
        sortable: true,
        orderBy: 'scan__blocker_violations',
        headerTooltip: 'Blocker Violations.',
        overrideContents: (rowData) => rowData?.scan?.blocker_violations,
        rowCellStyle: { textAlign: 'center' },
      },
      {
        key: 'vulnerabilities',
        label: 'Vuln',
        sortable: true,
        orderBy: 'scan__synced_vuln_count',
        headerTooltip: 'Number of vulnerabilities.',
        overrideContents: (rowData) => (
          <a
            className={`badge w-100 ${
              rowData?.scan?.vulnerabilities ===
              rowData?.scan?.synced_vuln_count
                ? 'badge-primary'
                : 'badge-warning'
            }`}
          >
            {' '}
            {rowData?.scan?.synced_vuln_count}{' '}
          </a>
        ),
        rowCellStyle: { textAlign: 'center', color: '#ffffff' },
        tooltip: (rowData) =>
          !rowData?.is_active ? (
            <>
              Drill down to vulnerabilities page is disabled for inactive
              repositories
            </>
          ) : rowData?.scan?.vulnerabilities >
            rowData?.scan?.synced_vuln_count ? (
            <>
              <span
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <strong>
                  <label style={{ textAlign: 'left' }}>
                    Actual Vulnerabilities: {rowData?.scan?.vulnerabilities}
                  </label>
                </strong>
                <strong>
                  <label style={{ textAlign: 'right' }}>
                    Synced Vulnerabilities: {rowData?.scan?.synced_vuln_count}
                  </label>
                </strong>
              </span>
              <p style={{ textAlign: 'left' }}>
                Due to rate limit for large sized repository, System was able to
                fetch only a limited number of bugs.
              </p>
            </>
          ) : undefined,
        onClick: (rowData) => {
          if (rowData?.is_active && rowData['account']['is_active']) {
            history.push(
              getDrillDownUrl({
                type: 'open',
                sourceType: 'sast2',
                hosts_in: [rowData.id],
                severity: '',
                issueType: 'VULNERABILITY',
                qid: '',
                severity_in: undefined,
              }),
              // '/' +
              //   atob(localStorage.getItem(btoa('approle')) ?? '') +
              //   `/qualys/vulnerabilities?type=open&source_type=sast2&issue_type=VULNERABILITY&sast_id=${rowData['id']}`,
            );
          }
        },
      },
      {
        key: 'bugs_count',
        label: 'Bugs',
        sortable: true,
        orderBy: 'scan__synced_bugs_count',
        headerTooltip: 'Number of Bugs.',
        overrideContents: (rowData) => (
          <a
            className={`badge w-100 ${
              rowData?.scan?.bugs_count === rowData?.scan?.synced_bugs_count
                ? 'badge-primary'
                : 'badge-warning'
            }`}
          >
            {' '}
            {rowData?.scan?.synced_bugs_count}{' '}
          </a>
        ),
        rowCellStyle: { textAlign: 'center', color: '#ffffff' },
        tooltip: (rowData) =>
          !rowData?.is_active ? (
            <>
              Drill down to vulnerabilities page is disabled for inactive
              repositories
            </>
          ) : rowData?.scan?.bugs_count > rowData?.scan?.synced_bugs_count ? (
            <>
              <span
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <strong>
                  <label style={{ textAlign: 'left' }}>
                    Actual Bugs: {rowData?.scan?.bugs_count}
                  </label>
                </strong>
                <strong>
                  <label style={{ textAlign: 'right' }}>
                    Synced Bugs: {rowData?.scan?.synced_bugs_count}
                  </label>
                </strong>
              </span>
              <p style={{ textAlign: 'left' }}>
                Due to rate limit for large sized repository, System was able to
                fetch only a limited number of bugs.
              </p>
            </>
          ) : undefined,
        onClick: (rowData) => {
          if (rowData?.is_active && rowData['account']['is_active']) {
            history.push(
              getDrillDownUrl({
                type: 'open',
                sourceType: 'sast2',
                hosts_in: [rowData.id],
                severity: '',
                issueType: 'BUG',
                qid: '',
                severity_in: undefined,
              }),
              // '/' +
              //   atob(localStorage.getItem(btoa('approle')) ?? '') +
              //   `/qualys/vulnerabilities?type=open&source_type=sast2&issue_type=BUG&sast_id=${rowData['id']}`,
            );
          }
        },
      },
      {
        key: 'languages',
        label: 'Languages',
        sortable: false,
        headerTooltip:
          'Repository Languages (Scanned languages are shown in blue, not scanned languages are shown in yellow)',
        render: (rowData) => {
          const supportedLanguages = rowData?.languages?.supported_languages;
          const unsupportedLanguages =
            rowData?.languages?.unsupported_languages;

          return (
            <LanguageBadges
              supportedLanguages={supportedLanguages}
              unsupportedLanguages={unsupportedLanguages}
            />
          );
        },
        headerCellStyle: {
          textAlign: 'left',
          maxWidth: '280px',
          minWidth: '280px !important',
        },
        rowCellStyle: {
          textAlign: 'left',
          gap: '1px',
          minWidth: '280px !important',
          maxWidth: '280px',
        },
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (rowData) => (
          <Actions rowData={rowData} onLoading={setLoading} />
        ),
        headerCellStyle: {
          textAlign: 'center',
          maxWidth: '180px',
          minWidth: '150px !important',
        },
        rowCellStyle: {
          textAlign: 'center',
          minWidth: '150px !important',
          maxWidth: '180px',
        },
      },
    ],
    columnsDisplayConfig: {
      enabledColumnKeys: enabledColumnKeys,
      defaultColumnKeys: DEFAULT_REPOSITORY_LISTING_COLUMNS,
      onColumnsDisplayChange: (updatedColumnKeys) => {
        updateEnabledColumnKeys(updatedColumnKeys);
      },
    },
    rowConfig: {
      classRules: [
        {
          className: 'new-row',
          ruleFunction: (rowData) => isWithinLastMinute(rowData['created_at']),
        },
        {
          className: 'warning-row',
          ruleFunction: (rowData) =>
            !rowData.account.is_active &&
            rowData.account.token_info?.access_token_failed_attempts < 10,
        },
        {
          className: 'danger-row',
          ruleFunction: (rowData) =>
            !rowData.account.is_active &&
            rowData.account.token_info?.access_token_failed_attempts >= 10,
        },
        {
          className: 'applied-row',
          ruleFunction: (rowData) => rowData['isAppliedForDashboard'] === true,
        },
      ],
    },
    paginationConfig: {
      total_count: listingData?.count || 0,
      pageNumber: paginationConfig.page - 1, // page number expects a zero based index
      pageSize: paginationConfig.page_size,
      onPageChange: (newPage: number) => {
        // console.log("page number changed to " + newPage);
        // page number returns a zero based index, so incrementing by 1
        setPaginationConfig({
          page_size: paginationConfig.page_size,
          page: newPage + 1,
        });
        if (!enableSelectionAcrossPages) {
          // reset selected row ids on  change
          setSelectedRowIds([]);
        }
      },
      onPageSizeChange: (newPageSize: number) => {
        // console.log("page size changed to " + newPageSize);
        // reset page number on page size change
        setPaginationConfig({ page_size: newPageSize, page: 1 });

        // reset selected row ids on  change
        setSelectedRowIds([]);
      },
    },
    sortingConfig: {
      orderBy: orderby,
      order: sortOrder,
      onOrderByChange: (value: string) => setOrderBy(value),
      onOrderChange: (value) => setSortOrder(value),
    },

    rowSelectionConfig: {
      enableSelectionAcrossPages: enableSelectionAcrossPages,
      preSelectedRows: selectedRowIds,
      onSelectionChange: (updatedRowIds) => {
        setSelectedRowIds(updatedRowIds);
      },
    },
    // eslint-disable-next-line no-warning-comments
    //   TODO: add more configuration options for responsive columns display, for different screen sizes
  });

  const config = useMemo<DataGridConfig>(() => {
    // console.log('selectedRowIds', selectedRowIds);
    const repoRow = listingData?.results?.find(
      (row) => row['account']['id'] === accountDetailsModal.accountId,
    );
    if (repoRow) {
      setAccountDetailsModal({
        ...accountDetailsModal,
        isActive: repoRow['account']['is_active'],
      });
    }
    return getReposGridConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listingData,
    enabledColumnKeys,
    forceRerenderUI,
    paginationConfig,
    selectedRowIds,
    appliedSASTIds,
  ]);

  const selectedCount = () => {
    if (selectedRowIds instanceof Array) {
      return selectedRowIds.length;
    }

    return 0;
  };

  return (
    <PageLayout
      id="sast_repos_listing"
      heading={
        <Row>
          <Col md="auto" sm="12">
            <CardTitle>
              <h4>
                Repositories
                {listingData?.count ? (
                  <label id="counts" className="label btn">
                    {' '}
                    {listingData?.count}
                  </label>
                ) : (
                  <></>
                )}
              </h4>
            </CardTitle>
          </Col>
        </Row>
      }
      description="List of all repositories/branches."
      filteringOptions={<Filters />}
    >
      <DataGrid config={config} data={listingData?.results} loading={loading} />
      <Suspense fallback={<></>}>
        <StatusUpdater />
      </Suspense>
      {accountDetailsModal.open && (
        <AccountDetailModal
          accountId={accountDetailsModal.accountId}
          isOpen={accountDetailsModal.open}
          isActive={accountDetailsModal.isActive}
          isLoading={accountActionLoading}
          toggle={accountDetailsModal.toggle}
          handleActivateDeactivateAccount={handleActivateDeactivateAccount}
        />
      )}
    </PageLayout>
  );
};

export default RepositoryList;
